import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card } from "../components/ui/Card";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useAuth } from "../features/auth/AuthProvider";
import {
  useMembersOverview,
  type MemberOverviewRow,
} from "../features/members/api";
import { formatRelative } from "../lib/format";

type Range = 7 | 30 | 90;
type SortKey =
  | "name"
  | "activeLeads"
  | "newLeads"
  | "activitiesLogged"
  | "converted"
  | "conversionRate"
  | "unattended24h"
  | "avgFirstResponseSec"
  | "lastActivityAt";

export default function MembersPage() {
  const { user } = useAuth();
  const { current } = useWorkspace();
  const [days, setDays] = useState<Range>(30);
  const [sortKey, setSortKey] = useState<SortKey>("newLeads");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Sales should never reach this page even if they type the URL — mirror
  // the server-side requireRole("manager") gate so we don't render a noisy
  // 403 from the API. Gate via a flag so the hooks below still run
  // unconditionally.
  const denied = !!user && !user.isSuperadmin && user.role === "sales";

  const { data, isLoading, isError, error } = useMembersOverview(
    denied ? null : current?.slug ?? null,
    days
  );
  if (denied) {
    return <Navigate to="/dashboard" replace />;
  }

  const members = data?.members ?? [];
  const sorted = [...members].sort((a, b) => compareRows(a, b, sortKey, sortDir));

  const summary = summarise(members);

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir(k === "name" ? "asc" : "desc");
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">
            {current?.name ?? "—"} · performance over last {days} days
          </p>
        </div>
        <RangePills value={days} onChange={setDays} />
      </header>

      <div className="stat-grid">
        <StatTile label="Active members" value={members.length.toLocaleString()} />
        <StatTile label="New leads" value={summary.newLeads.toLocaleString()} />
        <StatTile
          label="Activities logged"
          value={summary.activities.toLocaleString()}
        />
        <StatTile
          label="Converted"
          value={summary.converted.toLocaleString()}
          accent="ok"
        />
        <StatTile
          label="Unattended >24h"
          value={summary.unattended.toLocaleString()}
          accent={summary.unattended > 0 ? "warn" : undefined}
        />
      </div>

      <Card title="Performance" subtitle="Click a member to see their detail page">
        {isLoading ? (
          <div className="skeleton-rows">Loading…</div>
        ) : isError ? (
          <div className="form-error">{(error as Error).message}</div>
        ) : members.length === 0 ? (
          <p className="muted">No active members in this workspace yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh
                    label="Name"
                    keyName="name"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                  <th>Role</th>
                  <SortableTh
                    label="Active"
                    keyName="activeLeads"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="New"
                    keyName="newLeads"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Activities"
                    keyName="activitiesLogged"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Converted"
                    keyName="converted"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Conv. %"
                    keyName="conversionRate"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Stale >24h"
                    keyName="unattended24h"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                  {/* Avg first reply intentionally hidden — kept in the API
                      payload and on MemberOverviewRow so we can re-enable
                      without a backend change. */}
                  <SortableTh
                    label="Last active"
                    keyName="lastActivityAt"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <tr key={m.userId}>
                    <td>
                      <Link to={`/members/${m.userId}`} className="row-link">
                        {m.name}
                      </Link>
                      <div className="row-sub">{m.email}</div>
                    </td>
                    <td>
                      <span className="badge">{m.role}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.activeLeads.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.newLeads.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.activitiesLogged.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.converted.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.newLeads === 0
                        ? "—"
                        : `${(m.conversionRate * 100).toFixed(1)}%`}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: m.unattended24h > 0 ? "var(--warn)" : undefined,
                      }}
                    >
                      {m.unattended24h.toLocaleString()}
                    </td>
                    <td title={m.lastActivityAt ?? ""}>
                      {m.lastActivityAt ? formatRelative(m.lastActivityAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function summarise(rows: MemberOverviewRow[]) {
  return rows.reduce(
    (acc, r) => ({
      newLeads: acc.newLeads + r.newLeads,
      activities: acc.activities + r.activitiesLogged,
      converted: acc.converted + r.converted,
      unattended: acc.unattended + r.unattended24h,
    }),
    { newLeads: 0, activities: 0, converted: 0, unattended: 0 }
  );
}

function compareRows(
  a: MemberOverviewRow,
  b: MemberOverviewRow,
  key: SortKey,
  dir: "asc" | "desc"
): number {
  const sign = dir === "asc" ? 1 : -1;
  if (key === "name") return sign * a.name.localeCompare(b.name);
  if (key === "lastActivityAt") {
    const av = a.lastActivityAt ? Date.parse(a.lastActivityAt) : 0;
    const bv = b.lastActivityAt ? Date.parse(b.lastActivityAt) : 0;
    return sign * (av - bv);
  }
  // numeric (avgFirstResponseSec may be null — treat as Infinity desc so
  // "—" rows sink to the bottom regardless of direction)
  const av = a[key] ?? Number.POSITIVE_INFINITY;
  const bv = b[key] ?? Number.POSITIVE_INFINITY;
  if (av === bv) return 0;
  return sign * ((av as number) - (bv as number));
}

export function formatDuration(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) {
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }
  const d = Math.floor(sec / 86400);
  const h = Math.round((sec % 86400) / 3600);
  return h === 0 ? `${d}d` : `${d}d ${h}h`;
}

// ────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────

export function RangePills({
  value,
  onChange,
}: {
  value: Range;
  onChange: (r: Range) => void;
}) {
  const opts: Range[] = [7, 30, 90];
  return (
    <div className="range-pills" role="tablist" aria-label="Time range">
      {opts.map((d) => (
        <button
          key={d}
          role="tab"
          aria-selected={value === d}
          className={`range-pill${value === d ? " active" : ""}`}
          onClick={() => onChange(d)}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

function SortableTh({
  label,
  keyName,
  sortKey,
  sortDir,
  onClick,
  align,
}: {
  label: string;
  keyName: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onClick: (k: SortKey) => void;
  align?: "right";
}) {
  const active = sortKey === keyName;
  const arrow = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th
      onClick={() => onClick(keyName)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        textAlign: align ?? "left",
        whiteSpace: "nowrap",
      }}
      title="Click to sort"
    >
      {label}
      <span className="muted" style={{ fontSize: 11 }}>
        {arrow}
      </span>
    </th>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ok" | "warn" | "err";
}) {
  return (
    <div className={`stat-tile ${accent ? `accent-${accent}` : ""}`.trim()}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}
